import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import NonEditableField from "../NonEditableField/NonEditableField";
import { Form, Field, Formik } from "formik";
import RadioGroup from "../FormikFields/RadioGroup";
import Select from "../FormikFields/Select";
import {
  courseLocationTypes,
  timeZones,
  trainingObjectStatusIds
} from "../../constants/index";
import DatePickerField from "../FormikFields/DatePickerField";
import TimePickerField from "../FormikFields/TimePickerField";
import AutosuggestField from "../FormikFields/AutosuggestField";
import { TextField } from "formik-mui";
import * as Yup from "yup";
import { findByKey } from "../../utils/helpers";
import { startDateValidation } from "../../utils/validation";
import { find, sortBy } from "lodash";
import {
  isValidDate,
  isBeforeDate,
  isBeforeTime,
  isDayEqual
} from "../../utils/datetime";
import moment from "moment-timezone";

// the course status IDs need to be strings because radio button values must be strings
const activeStatus = `${trainingObjectStatusIds.active}`;
const inactiveStatus = `${trainingObjectStatusIds.inactive}`;

/**
 * When the user sets "Is Active" to "No", we want to update the session to be inactive, (status type id 2)
 * only if the course is currently active or inactive. Otherwise if the course is currently closed out,
 * we want to set the session status to closed out (status type id 3) when they select "No"
 */
const getInactiveStatusTypeId = initialStatusTypeId =>
  initialStatusTypeId === activeStatus ? inactiveStatus : initialStatusTypeId;

const getDefaultOverviewText = (iltLocationType, t) => {
  return iltLocationType === courseLocationTypes.virtualClassroom
    ? t("manageCourses.virtualClassroomDefaultCourseOverview")
    : t("manageCourses.defaultCourseOverview");
};

const getDefaultInstructor = (instructorList, instructorId) => {
  const instructor = find(instructorList, { id: instructorId });
  return instructor ? instructor : "";
};
const getScheduleSessionSchema = (t, locations, instructors) => {
  return Yup.object().shape({
    virtualClassroomId: Yup.mixed().test(
      "virtualClassroomId",
      t("validationMessages.classroomRequired"),
      function() {
        return (
          this.parent.locationType !== courseLocationTypes.virtualClassroom ||
          !!this.parent.virtualClassroomId
        );
      }
    ),
    seats: Yup.number()
      .integer(t("validationMessages.mustBeInteger"))
      .test("seats", t("validationMessages.validNumber"), function() {
        return typeof this.parent.seats === "number" && this.parent.seats > 0;
      })
      .test(
        "seatsVirtualClassroom",
        t("validationMessages.cannotExceedCapacity"),
        function() {
          return this.parent.locationType ===
            courseLocationTypes.virtualClassroom
            ? this.parent.seats <= 500
            : true;
        }
      )
      .test(
        "seatsInPerson",
        t("validationMessages.iltLocationCapacity"),
        function() {
          return this.parent.locationId &&
            this.parent.locationType === courseLocationTypes.inPerson
            ? this.parent.seats <=
                findByKey(locations, "id", this.parent.locationId, "seats")
            : true;
        }
      ),
    locationId: Yup.mixed().test(
      "locationId",
      t("validationMessages.locationRequired"),
      function() {
        return (
          this.parent.locationType !== courseLocationTypes.inPerson ||
          !!this.parent.locationId
        );
      }
    ),
    /** The value for instructor will be the ID of the instructor when
     * the session data is initially fetched. If the user selects a new instructor,
     * the value for the instructor will be an object with the instructor's details. At some
     * point ideally we should probably make instructor be a relationship for the training object.
     **/
    instructor: Yup.mixed()
      .required(t("validationMessages.fieldRequired"))
      .test(
        "validInstructor",
        t("validationMessages.validInstructor"),
        function() {
          const { instructor } = this.parent;
          return (
            instructor &&
            !!instructors.find(
              ({ id }) => id === instructor || id === instructor.id
            )
          );
        }
      ),
    startDate: startDateValidation(t, true),
    endDate: Yup.mixed()
      .required(t("validationMessages.fieldRequired"))
      .test("validEndDate", t("validationMessages.validDate"), function() {
        return (
          isValidDate(this.parent.endDate) &&
          !isBeforeDate(this.parent.endDate, "01/01/1900")
        );
      }),
    startTime: Yup.mixed()
      .required(t("validationMessages.fieldRequired"))
      .test("validStartTime", t("validationMessages.validTime"), function() {
        return isValidDate(this.parent.startTime);
      })
      .test(
        "startTime",
        t("validationMessages.startTimeAfterEndTime"),
        function() {
          const { startDate, endDate, startTime, endTime } = this.parent;
          return (
            !isDayEqual(startDate, endDate) || isBeforeTime(startTime, endTime)
          );
        }
      ),
    endTime: Yup.mixed()
      .required(t("validationMessages.fieldRequired"))
      .test("validEndTime", t("validationMessages.validTime"), function() {
        return isValidDate(this.parent.endTime);
      }),
    timeZone: Yup.string().required(t("validationMessages.fieldRequired")),
    overviewText: Yup.mixed().when("showOverview", {
      is: true,
      then: Yup.mixed()
        .required(t("validationMessages.fieldRequired"))
        .test(
          "validOverviewTextLength",
          t("validationMessages.cannotExceedLength", {
            type: "Course Instructions & Learning Objectives",
            maxLength: 800
          }),
          function() {
            return (
              !this.parent.overviewTextOnlyLength ||
              this.parent.overviewTextOnlyLength <= 800
            );
          }
        )
    })
  });
};

const InstructorMenuItem = ({ suggestion }) => {
  return (
    <Box disolay="flex" flexDirection="column">
      <Typography variant="subtitle2">{suggestion.text}</Typography>
      <Typography variant="body2" sx={{ fontStyle: "italic" }}>
        {suggestion.location}
      </Typography>
    </Box>
  );
};

const IltSessionForm = ({
  children,
  locations,
  setFieldValue,
  locationType,
  locationId,
  virtualClassroomId,
  allowSeatsFieldUpdate,
  startTime,
  startDate,
  endDate,
  duration
}) => {
  useEffect(() => {
    if (allowSeatsFieldUpdate) {
      let seats = "";
      if (locationType === courseLocationTypes.virtualClassroom) {
        seats = 500;
      } else if (locationType === courseLocationTypes.inPerson && locationId) {
        seats = findByKey(locations, "id", locationId, "seats");
      }
      setFieldValue("seats", seats);
    }
    if (
      locationType === courseLocationTypes.online ||
      locationType === courseLocationTypes.virtualClassroom
    ) {
      setFieldValue("locationId", null);
    }
    if (
      locationType === courseLocationTypes.online ||
      locationType === courseLocationTypes.inPerson
    ) {
      setFieldValue("virtualClassroomId", null);
    }
  }, [locationType, locationId, virtualClassroomId]);

  useEffect(() => {
    if (startTime && isValidDate(startTime) && !!duration) {
      const newEndTime = moment(startTime);

      newEndTime.add(duration, "minutes");
      setFieldValue("endTime", newEndTime);
    }
  }, [startTime]);

  useEffect(() => {
    if (startDate && isValidDate(startDate) && !endDate) {
      setFieldValue("endDate", startDate);
    }
  }, [startDate]);

  return <Form data-qa="forms-ilt-session-form">{children}</Form>;
};

IltSessionForm.propTypes = {
  children: PropTypes.node,
  locationId: PropTypes.string,
  locationType: PropTypes.string,
  locations: PropTypes.array,
  setFieldValue: PropTypes.func,
  /**
   * When the form is initially loaded, if the seats field already has an initial value,
   * we don't want to automatically update the seats field value until the user
   * updates the location type or location ID. When they update the location type
   * or location ID, we update the seats to either 500 (for virtual classrooms)
   * or the capacity of the training location (for in-person classes)
   */
  allowSeatsFieldUpdate: PropTypes.bool
};

const IltSessionFormContainer = ({
  render,
  initialValues,
  iltName,
  trainingHours,
  description,
  instructors,
  virtualClassrooms,
  locations,
  onSubmit,
  disabled,
  virtualClassroomAllowed,
  duration
}) => {
  const { t } = useTranslation();

  const timeZoneMenuItems = Object.keys(timeZones).map(zone => ({
    value: timeZones[zone],
    label: t(`scheduleSession.timeZones.${zone}`)
  }));

  const virtualClassroomMenuItems = virtualClassrooms
    ? virtualClassrooms.map(classroom => ({
        value: classroom.id,
        label: classroom.name
      }))
    : [];

  const virtualClassroomsAvailable = () =>
    virtualClassroomMenuItems.length > 0 && virtualClassroomAllowed;
  const notVirtualClassroomOption = type =>
    courseLocationTypes.virtualClassroom !== courseLocationTypes[type];

  const locationTypeMenuItems = Object.keys(courseLocationTypes)
    .filter(
      type => virtualClassroomsAvailable() || notVirtualClassroomOption(type)
    )
    .map(type => ({
      value: courseLocationTypes[type],
      label: t(`scheduleSession.locationTypes.${type}`)
    }));

  const iltLocationMenuItems = locations
    ? sortBy(
        locations,
        location => location.name && location.name.toLowerCase()
      ).map(location => ({
        value: location.id,
        label: location.name
      }))
    : [];

  const instructorsList =
    instructors &&
    instructors
      .filter(
        instructor =>
          "firstName" in instructor &&
          "lastName" in instructor &&
          "city" in instructor &&
          "state" in instructor
      )
      .map(instructor => ({
        text: `${instructor.firstName} ${instructor.lastName}`,
        location: `${instructor.city}, ${instructor.state}`,
        id: instructor.id
      }));

  const {
    statusTypeId,
    displayInLibrary,
    hideFromLibraryAfter,
    locationType,
    seats,
    locationId,
    virtualClassroomId,
    instructor,
    startDate,
    endDate,
    startTime,
    endTime,
    timeZone,
    showOverview,
    overviewText,
    overviewMedia,
    hasActiveAssignments
  } = initialValues;

  // Have to convert to string from number because radio button value must be string
  const initialStatusTypeId = `${statusTypeId}`;

  const handleLocationTypeChange = (setFieldValue, newLocationType) => {
    setFieldValue("overviewText", getDefaultOverviewText(newLocationType, t));
    setFieldValue("locationType", newLocationType);
    if (locationType === courseLocationTypes.virtualClassroom) {
      setFieldValue("showOverview", true);
    }
  };

  return (
    <Formik
      validationSchema={getScheduleSessionSchema(t, locations, instructorsList)}
      initialValues={{
        showOverview,
        overviewText: overviewText
          ? overviewText
          : getDefaultOverviewText(locationType, t),
        overviewMedia: overviewMedia,
        statusTypeId: initialStatusTypeId,
        displayInLibrary: `${displayInLibrary}`,
        hideFromLibraryAfter: hideFromLibraryAfter || "end",
        locationType: locationType ? locationType : "",
        seats: seats ? seats : "",
        locationId: locationId ? locationId : "",
        virtualClassroomId: virtualClassroomId ? virtualClassroomId : "",
        instructor: instructor ? instructor : "",
        startDate: startDate ? startDate : null,
        endDate: endDate ? endDate : null,
        timeZone: timeZone ? timeZone : "",
        startTime: startTime ? startTime : null,
        endTime: endTime ? endTime : null
      }}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        isSubmitting,
        values,
        resetForm,
        submitForm,
        setFieldValue,
        touched
      }) => (
        <IltSessionForm
          locationType={values.locationType}
          locationId={values.locationId}
          virtualClassroomId={values.virtualClassroomId}
          startDate={values.startDate}
          startTime={values.startTime}
          endDate={values.endDate}
          endTime={values.endTime}
          locations={locations}
          setFieldValue={setFieldValue}
          duration={duration}
          allowSeatsFieldUpdate={
            !seats ||
            touched.locationType ||
            touched.virtualClassroomId ||
            touched.locationId
          }
        >
          {render({
            formFields: {
              name: (
                <NonEditableField
                  align="flex-start"
                  fieldName={t("scheduleSession.nameLabel")}
                  fieldValue={iltName}
                />
              ),
              trainingHours: (
                <NonEditableField
                  align="flex-start"
                  fieldName={t("scheduleSession.trainingHoursLabel")}
                  fieldValue={trainingHours}
                />
              ),
              description: (
                <NonEditableField
                  align="flex-start"
                  fieldName={t("scheduleSession.descriptionLabel")}
                  fieldValue={description}
                />
              ),
              isActive: (
                <RadioGroup
                  radioButtons={[
                    { label: t("yes"), value: activeStatus },
                    {
                      label: t("no"),
                      value: getInactiveStatusTypeId(initialStatusTypeId)
                    }
                  ]}
                  name="statusTypeId"
                  label={t("scheduleSession.isActiveLabel")}
                  disabled={
                    disabled ||
                    isSubmitting ||
                    (initialStatusTypeId === activeStatus &&
                      hasActiveAssignments)
                  }
                />
              ),
              displayInLibrary: (
                <RadioGroup
                  radioButtons={[
                    { label: t("yes"), value: "true" },
                    { label: t("no"), value: "false" }
                  ]}
                  name="displayInLibrary"
                  label={t("scheduleSession.displayInLibraryLabel")}
                  disabled={disabled || isSubmitting}
                />
              ),
              hideFromLibraryAfter: (
                <RadioGroup
                  radioButtons={[
                    {
                      label: t("scheduleSession.startTimeLabel"),
                      value: "start"
                    },
                    { label: t("scheduleSession.endTimeLabel"), value: "end" }
                  ]}
                  name="hideFromLibraryAfter"
                  label={t("scheduleSession.hideFromLibraryAfterLabel")}
                  disabled={disabled || isSubmitting}
                />
              ),
              locationType: (
                <Select
                  name="locationType"
                  menuItems={locationTypeMenuItems}
                  onChange={newValue =>
                    handleLocationTypeChange(setFieldValue, newValue)
                  }
                  disabled={disabled || isSubmitting}
                />
              ),
              virtualClassroom: (
                <Select
                  name="virtualClassroomId"
                  menuItems={virtualClassroomMenuItems}
                  label={t("scheduleSession.classroomLabel")}
                  disabled={disabled || isSubmitting}
                />
              ),
              location: (
                <Select
                  name="locationId"
                  menuItems={iltLocationMenuItems}
                  label={t("scheduleSession.locationLabel")}
                  disabled={disabled || isSubmitting}
                />
              ),
              capacity: (
                <Field
                  fullWidth
                  label={t("scheduleSession.capacityLabel")}
                  disabled={disabled || isSubmitting}
                  variant="filled"
                  component={TextField}
                  name="seats"
                  type="number"
                  InputProps={{
                    inputProps: {
                      min: "1",
                      "data-qa": "schedule-session-capacity-field"
                    }
                  }}
                />
              ),
              instructor: (
                <AutosuggestField
                  disabled={disabled || isSubmitting}
                  placeholder={t("scheduleSession.instructorLabel")}
                  defaultValue={getDefaultInstructor(
                    instructorsList,
                    instructor
                  )}
                  name="instructor"
                  renderSuggestionText={suggestion => (
                    <InstructorMenuItem suggestion={suggestion} />
                  )}
                  suggestions={instructorsList}
                />
              ),
              startDate: (
                <Field
                  name="startDate"
                  component={DatePickerField}
                  label={t("scheduleSession.startDateLabel")}
                  disabled={disabled || isSubmitting}
                  dataQa="ilt-session-start-date-field"
                  inputDataTestId="ilt-session-start-date-field"
                />
              ),
              startTime: (
                <Field
                  name="startTime"
                  component={TimePickerField}
                  label={t("scheduleSession.startTimeLabel")}
                  disabled={disabled || isSubmitting}
                  data-qa="ilt-session-start-time-field"
                  inputProps={{ "data-testid": "ilt-session-start-time-field" }}
                />
              ),
              endDate: (
                <Field
                  name="endDate"
                  minDate={values.startDate}
                  component={DatePickerField}
                  label={t("scheduleSession.endDateLabel")}
                  disabled={disabled || isSubmitting}
                  dataQa="ilt-session-end-date-field"
                  inputDataTestId="ilt-session-end-date-field"
                />
              ),
              endTime: (
                <Field
                  name="endTime"
                  component={TimePickerField}
                  label={t("scheduleSession.endTimeLabel")}
                  disabled={disabled || isSubmitting}
                  data-qa="ilt-session-end-time-field"
                  inputProps={{ "data-testid": "ilt-session-end-time-field" }}
                />
              ),
              timeZone: (
                <Select
                  name="timeZone"
                  menuItems={timeZoneMenuItems}
                  label={t("scheduleSession.timeZoneLabel")}
                  disabled={disabled || isSubmitting}
                />
              )
            },
            formikProps: {
              resetForm,
              values,
              submitForm,
              isSubmitting
            }
          })}
        </IltSessionForm>
      )}
    </Formik>
  );
};

IltSessionFormContainer.propTypes = {
  initialValues: PropTypes.shape({
    seats: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    locationId: PropTypes.string,
    instructor: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    timeZone: PropTypes.string,
    locationType: PropTypes.string,
    statusTypeId: PropTypes.oneOf(Object.values(trainingObjectStatusIds)),
    displayInLibrary: PropTypes.string,
    hideFromLibraryAfter: PropTypes.string
  }),
  description: PropTypes.string,
  iltName: PropTypes.string,
  trainingLocationId: PropTypes.number,
  virtualClassroomAllowed: PropTypes.bool,
  trainingHours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSubmit: PropTypes.func,
  render: PropTypes.func,
  instructors: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string
    })
  ),
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      seats: PropTypes.number
    })
  ),
  virtualClassrooms: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      id: PropTypes.string,
      name: PropTypes.string
    })
  )
};

export default IltSessionFormContainer;
